import { computed, ref, readonly } from 'vue'

import { hasStaffSession } from '@/services/auth'
import { isInterestTerm, type InterestTerm } from '@/types/interest.types'
import { fetchMetadata, type MetadataReferenceValue, type MetadataResponse } from '@/services/metadata'

const metadata = ref<MetadataResponse | null>(null)
const isLoading = ref(false)
const error = ref('')
let fetchPromise: Promise<void> | null = null

const loadMetadata = async () => {
  if (metadata.value) return
  if (fetchPromise) return fetchPromise
  if (!hasStaffSession()) return

  fetchPromise = (async () => {
    isLoading.value = true
    error.value = ''

    try {
      metadata.value = await fetchMetadata()
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Kunde inte hämta metadata.'
    } finally {
      isLoading.value = false
      fetchPromise = null
    }
  })()

  return fetchPromise
}

const getReferenceValues = (key: string): MetadataReferenceValue[] => {
  return metadata.value?.references[key]?.values ?? []
}

const translateRef = (reference: string, code: string | undefined) => {
  return getReferenceValues(reference).find((item) => item.code === code)?.label ?? code ?? ''
}

/** The field's label, or its name while the metadata hasn't loaded. */
const fieldLabel = (resource: string, field: string) =>
  metadata.value?.resources[resource]?.fields[field]?.label ?? field

const translateInterestTerm = (term: string) => {
  return getReferenceValues('debtInterestTerms').find((item) => item.code === term)?.label ?? term
}
const translateDocumentType = (type: string) => translateRef('documentTypes', type)

export const useMetadata = () => {
  return {
    metadata: readonly(metadata),
    isLoading: readonly(isLoading),
    error: readonly(error),
    loadMetadata,
    topupPurposeOptions: computed(() => getReferenceValues('topupPurposes')),
    cashContributionOptions: computed(() => getReferenceValues('cashContributionSources')),
    debtInterestTermOptions: computed(() => getReferenceValues('debtInterestTerms')),
    interestTermOptions: computed(() =>
      getReferenceValues('debtInterestTerms')
        .filter((item) => isInterestTerm(item.code))
        .map((item) => ({ value: item.code as InterestTerm, text: item.label })),
    ),
    getReferenceValues,
    translateRef,
    fieldLabel,
    translateDocumentType,
    translateInterestTerm,
  }
}
